import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfirm } from 'material-ui-confirm'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'

import { ThemeContext, UserContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

import MascotForm from '../MascotForm'

export default function MascotDetailsPage() {
  const theme = useContext(ThemeContext)
  // Editing/deleting a mascot is superuser-only (#290); staff get a read-only view.
  const currentUser = useContext(UserContext)
  const isSuperuser = currentUser && currentUser.role === "superuser"
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { mascotId } = useParams()

  const [mascot, setMascot] = useState(null)
  const form = useRef({ payload: () => ({}), isValid: false })
  const [isValid, setIsValid] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    rpc.post(
      "",
      buildRequest("admin.mascots.get", { mascot_id: Number(mascotId) })
    ).then(response => {
      if (!response.data.result.message) {
        setMascot(response.data.result)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message)
      }
    })
  }, [mascotId])

  const onFormChange = useCallback(({ payload, isValid }) => {
    form.current = { payload: () => payload, isValid }
    setIsValid(isValid)
  }, [])

  const submitForm = () => {
    if (!form.current.isValid) {
      return
    }
    rpc.post(
      "",
      buildRequest("admin.mascots.update", {
        mascot_id: Number(mascotId),
        ...form.current.payload(),
      })
    ).then(response => {
      if (!response.data.result.message) {
        setMascot(response.data.result)
        setErrorMessage(null)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setErrorMessage(response.data.result.message)
      }
    })
  }

  const deleteMascot = () => {
    confirm({ description: `Delete mascot "${mascot.name}" (${mascot.slug})?` }).then(() => {
      rpc.post(
        "",
        buildRequest("admin.mascots.delete", { mascot_id: Number(mascotId) })
      ).then(response => {
        if (response.data.result.success) {
          navigate("/mascots")
        } else {
          setErrorMessage(response.data.result.message)
        }
      })
    }).catch(() => { /* cancelled */ })
  }

  if (!mascot) {
    return (
      <Container fixed>
        <CircularProgress sx={{ marginTop: 4 }} />
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Mascot: ${mascot.slug}`}</title>
      </Helmet>

      {isSuperuser && (
        <Fab
          color="primary"
          aria-label="save"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          disabled={!isValid}
          onClick={submitForm}
        >
          <SaveIcon />
        </Fab>
      )}

      <Container fixed>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            Edit Mascot — {mascot.slug}
          </Typography>
          {isSuperuser && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={deleteMascot}>
              Delete
            </Button>
          )}
        </Stack>
        <MascotForm initial={mascot} errorMessage={errorMessage} onChange={onFormChange} />
      </Container>
    </>
  )
}
